import React, { useState } from "react"
import { Tooltip } from "react-tooltip"
import Money from "components/Money"
import DineroFactory from "dinero.js"
import dayjs from "dayjs"
import { getCountryName } from "helpers/countries"
import { SessionGroup } from "./types"
import { ExpandButton, Reference } from "./CustomerOrderTable.styled"
import CustomerCheckoutSessionRow from "./CustomerCheckoutSessionRow"
import SessionStatus from "./SessionStatus"
import { ReactComponent as DownIcon } from "images/icons/chevron-down.svg"
import {
  TableRow,
  Section,
  DateTime,
  TotalAmount,
  AmountUpIndicator,
  AmountDownIndicator
} from "./CustomerCheckoutSessionTable.styled"
import { CustomerOrders } from "../../classes/customer/CustomerOrders"

type Props = {
  sessionGroup: SessionGroup
  customerOrders: CustomerOrders
}

const CustomerSessionGroupRow = ({ sessionGroup, customerOrders }: Props) => {
  const [expanded, setExpanded] = useState(false)

  const toggleExpand = () => {
    setExpanded(!expanded)
  }

  // Calculate value change information (increase or decrease)
  const getValueChangeInfo = () => {
    if (sessionGroup.checkouts.length < 2) return null

    // Sort checkouts by creation date
    const sortedCheckouts = sessionGroup.checkouts.sort((a, b) =>
      new Date(a.created).getTime() - new Date(b.created).getTime()
    )

    const firstCheckout = sortedCheckouts[0]
    const latestCheckout = sortedCheckouts[sortedCheckouts.length - 1]

    const firstTotal = firstCheckout.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0
    const latestTotal = latestCheckout.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0

    if (latestTotal !== firstTotal) {
      const change = latestTotal - firstTotal
      const changePercentage = Math.round((Math.abs(change) / firstTotal) * 100)
      return {
        change,
        changePercentage,
        isIncrease: change > 0,
        currencyCode: latestCheckout.currencyCode
      }
    }

    return null
  }

  const valueChangeInfo = getValueChangeInfo()

  return (
    <>
      <TableRow style={{ cursor: 'pointer' }} onClick={toggleExpand}>
        <Reference>
          <ExpandButton $expanded={expanded} style={{ border: 'none', background: 'transparent' }}>
            <DownIcon />
          </ExpandButton>
          <div>
            <DateTime>
              <div>
            <span>
              <b>{dayjs(sessionGroup.latestCreated).format("YYYY-MM-DD")}</b>
            </span>
                <span>{dayjs(sessionGroup.latestCreated).format("HH:mm")}</span>
              </div>
            </DateTime>
          </div>
        </Reference>

        <Section>
          {valueChangeInfo && (
            <>
              <Tooltip id={`value-change-${sessionGroup.sessionId}`}>
                <div>
                  <strong>
                    {valueChangeInfo.isIncrease ? 'Checkout value increased!' : 'Checkout value decreased'}
                  </strong><br />
                  Value {valueChangeInfo.isIncrease ? 'increased' : 'decreased'} by {' '}
                  <Money
                    amount={Math.abs(valueChangeInfo.change)}
                    currencyUnit={valueChangeInfo.currencyCode as DineroFactory.Currency}
                  />
                  {' '}({valueChangeInfo.isIncrease ? '+' : '-'}{valueChangeInfo.changePercentage}%)
                </div>
              </Tooltip>
              {valueChangeInfo.isIncrease ? (
                <AmountUpIndicator data-tooltip-id={`value-change-${sessionGroup.sessionId}`}>
                  +
                </AmountUpIndicator>
              ) : (
                <AmountDownIndicator data-tooltip-id={`value-change-${sessionGroup.sessionId}`}>
                  −
                </AmountDownIndicator>
              )}
            </>
          )}
        </Section>
        <Section className="hideOnMediumScreens">
          {customerOrders?.getStoreGroupName(sessionGroup.checkouts[0]?.storeGroupId) || '-'}
        </Section>

        <Section className={"hideOnMediumScreens"}>
          {getCountryName(sessionGroup.checkouts[0]?.countryCode || '')}
        </Section>

        <Section className="hideOnSmallScreens">
          <div>
            <TotalAmount>
              {(() => {
                const latestCheckout = sessionGroup.checkouts.sort((a, b) => new Date(b.created).getTime() - new Date(a.created).getTime())[0]
                const latestTotal = latestCheckout?.items?.reduce((sum, item) => sum + (item.totalPriceAmount || 0), 0) || 0
                return (
                  <Money
                    amount={latestTotal}
                    currencyUnit={latestCheckout?.currencyCode as DineroFactory.Currency || 'USD'}
                  />
                )
              })()}
            </TotalAmount>
          </div>
        </Section>
        <SessionStatus sessionGroup={sessionGroup} />
      </TableRow>

      {expanded && (
        <div>
          {sessionGroup.checkouts.map((checkout) => (
            <CustomerCheckoutSessionRow checkout={checkout} key={checkout.id} customerOrders={customerOrders} />
          ))}
        </div>
      )}
    </>
  )
}

export default CustomerSessionGroupRow
